import React, { forwardRef } from 'react'
import { css, cx } from '@linaria/core'

import { sanitizeProps } from '@fs/zion-frontend-friends'
import { useFeatureFlag } from '@fs/zion-flags'
import { linkCss } from '@fs/zion-router-base/internals-do-not-use'
import { textAcss } from '../../typography/typographyStyles'

import TextAndIconButton from '../TextAndIconButton'
import { lowEmphasisCss } from '../emphasisStyles'
import type { TextButtonProps, ButtonIconProps } from '../types'
import { colorCss, lowCss, grayLowCss } from '../colorStyles'

type InlineButtonProps = {
  /** Only text allowed for the children */
  children: string | string[]

  /**
   * The icon to use for the button.
   *
   * **DO NOT USE** inline with text (like in a Paragraph). It looks weird.
   */
  Icon?: ButtonIconProps['Icon']

  /** Whether the icon is at the beginning or end of the button */
  iconAtEnd?: boolean
} & Omit<TextButtonProps, 'children' | 'text'> &
  Omit<ButtonIconProps, 'Icon'>

const inlineButtonCss = css`
  border-radius: 4px;
  padding: 7px 4px;
  height: 28px;
  margin: -9px -4px;
  max-width: calc(100% + 16px);
  text-transform: uppercase;
  // Important to not become overridden by TextAndIconButton icon styles
  --icon-outside-margin: -1px !important;
  --icon-inside-margin: 2px !important;
`

const lowercaseButtonCss = css`
  border-radius: 4px;
  padding: 7px 4px;
  height: 28px;
  margin: -9px -4px;
  max-width: calc(100% + 16px);
  text-transform: capitalize;
  // Important to not become overridden by TextAndIconButton icon styles
  --icon-outside-margin: -1px !important;
  --icon-inside-margin: 2px !important;

  /* The following CSS is to override linkCss hover, visited, and focus states */
  &:hover,
  &:visited,
  &:visited:hover,
  &:focus-visible,
  &:focus {
    color: var(--button-text-color) !important;
  }

  // Ensure underlines from linkCss are preserved even with no-link-styles attribute
  &[no-link-styles] {
    text-decoration: underline !important;
  }
`

export const InlineButton = forwardRef(({ children, ...props }: InlineButtonProps, ref) => {
  const { isOn: lowercaseButton } = useFeatureFlag('frontier_zionui_lowercase_button')

  const classNames = [
    lowercaseButton ? linkCss : null,
    lowercaseButton ? lowercaseButtonCss : inlineButtonCss,
    lowEmphasisCss,
    colorCss,
    lowercaseButton ? grayLowCss : lowCss,
    textAcss,
  ]

  return (
    <TextAndIconButton {...sanitizeProps(props)} className={cx(...classNames)} ref={ref} iconSize="xs">
      {children}
    </TextAndIconButton>
  )
})

export default InlineButton
